import { MepGetLookupValuesResponse, MepLookupItem } from "../../../../../models/mep/mep.models";
import { MepLookupService } from "../../../../../services/mep/mep-lookup.service";
import {Lookups} from "../../../../../constants/mep/mep-lookup.constants";
import { AlphaNumericSortHelper } from "../../../../../helpers/alpha-numeric-sort-helper";
import { MonthYearType } from "../../../../components/comp-month-picker/comp-month-picker";

export class MepLookupFiltersHandler {
    portId: number;
    customerId: number;
    emissionTypes: MepLookupItem[];
    financialYears: MepLookupItem[];

    lookupsLoadedFunc: () => void;
    isLookupDataRefreshing: boolean = false;
    weightBuckets: MepLookupItem[];
    pointsOfInterests: MepLookupItem[];
    vesselTypeGroups: MepLookupItem[];
    operationModes: MepLookupItem[];
    cargoTypes: MepLookupItem[];
    selectedYear: number;
    selectedMonths: MonthYearType[];
    scopes: MepLookupItem[];
    ports: MepLookupItem[];

    constructor(portId: number, lookupsLoadedFunc: () => void,
        private mepLookupService: MepLookupService,
        customerId?: number) {
        this.portId = portId;
        this.lookupsLoadedFunc = lookupsLoadedFunc;
        this.customerId = customerId;
    }

    includeWeightBucketsLookup: boolean;
    includePortsLookup: boolean;

    skipRefreshOfLookupsBecauseOfInit: boolean = false;
    initLookups(queries: string[] = null) {
        return new Promise<string>((resolve, reject) => {
            this.isLookupDataRefreshing = true;
            this.mepLookupService.getLookups({
                portId: this.portId,
                queries: queries ?? this.getLookupQueries(),
                emissionTypesIncludeAllYears: false,
                years: this.selectedYear ? [this.selectedYear] : null
            }).then(response => {
                this.isLookupDataRefreshing = false;
                if (response.hasError) {
                    reject();
                } else {
                    this.setLookupsFromResponse(response);
                    this.skipRefreshOfLookupsBecauseOfInit = true; // Skip the refresh lookup which will be caused by onSelectedMonthsChanged since the data is filtered by year already
                }

                resolve(null);
            }).catch(() => {
                this.isLookupDataRefreshing = false;
                reject();
            })
        });
    }

    initLookupsByCustomer(queries: string[] = null, customerId: number) {
        return new Promise<string>((resolve, reject) => {
            this.isLookupDataRefreshing = true;
            this.mepLookupService.getLookupsByCustomer({
                customerId: customerId ?? null,
                years: this.selectedYear ? [this.selectedYear] : null,
                queries: queries ?? this.getLookupQueries(),
                emissionTypesIncludeAllYears: false,
            }).then(response => {
                this.isLookupDataRefreshing = false;
                if (response.hasError) {
                    reject();
                } else {
                    this.setLookupsFromResponse(response);
                }

                resolve(null);
            }).catch(() => {
                this.isLookupDataRefreshing = false;
                reject();
            })
        });
    }

    onSelectedMonthsChanged(): Promise<string> {
        let refreshLookups = null;
        if (!this.skipRefreshOfLookupsBecauseOfInit) { // skipRefreshOfLookupsBecauseOfInit since the first time initLookups is called which already has defaults set 
            refreshLookups = this.refreshLookups();
        }
        this.skipRefreshOfLookupsBecauseOfInit = false;//reset this so that on selected year change we fetch new lookups 
        return refreshLookups;
    }

    refreshLookups() {
        return new Promise<string>((resolve, reject) => {
            this.isLookupDataRefreshing = true;
            this.mepLookupService.getLookups({
                portId: this.portId,
                years: this.selectedYear ? [this.selectedYear] : null,
                queries: this.getLookupQueries(),
                emissionTypesIncludeAllYears: false
            }).then(response => {
                this.isLookupDataRefreshing = false;
                if (response.hasError) {
                    reject();
                } else {
                    this.setLookupsFromResponse(response);
                }
                resolve(null);
            }).catch(() => {
                this.isLookupDataRefreshing = false;
                reject();
            });
        });
    }

    private setLookupsFromResponse(response: MepGetLookupValuesResponse) {
        this.emissionTypes = response.allValuesByQuery[Lookups.EmissionTypes];
        this.financialYears = response.allValuesByQuery[Lookups.FinancialYears];
        this.vesselTypeGroups = response.allValuesByQuery[Lookups.VesselTypeGroups];
        this.pointsOfInterests = response.allValuesByQuery[Lookups.PointOfInterests];
        if(this.pointsOfInterests)
        {
            this.pointsOfInterests = this.pointsOfInterests.sort((a,b) => AlphaNumericSortHelper.alphaNumericSort(a, b, 'name'));
        }
        this.operationModes = response.allValuesByQuery[Lookups.OperationModes];
        this.cargoTypes = response.allValuesByQuery[Lookups.CargoTypes];
        this.scopes = response.allValuesByQuery[Lookups.Scopes];
        if (this.includeWeightBucketsLookup) {
            this.weightBuckets = response.allValuesByQuery[Lookups.WeightBuckets]
        }

        if (this.includePortsLookup) {
            this.ports = response.allValuesByQuery[Lookups.Ports];
        }
    }

    private getLookupQueries() {
        const queries = [
            Lookups.EmissionTypes,
            Lookups.OperationModes,
            Lookups.VesselTypeGroups,
            Lookups.CargoTypes,
            Lookups.PointOfInterests,
            Lookups.Scopes
        ];

        if (this.includeWeightBucketsLookup) {
            queries.push(Lookups.WeightBuckets);
        }

        if (this.includePortsLookup) {
            queries.push(Lookups.Ports);
        }
        return queries;
    }

}
